import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Home, Settings as SettingsIcon, Building2, Palette, FileText, FileBarChart, Stethoscope, SlidersHorizontal, Cog, UserPlus } from 'lucide-react';
import GeneralSettings from './GeneralSettings';
import BrandingSettings from './BrandingSettings';
import InvoiceTemplateSettings from './InvoiceTemplateSettings';
import ReportTemplateSettings from './ReportTemplateSettings';
import PrescriptionTemplateSettings from './PrescriptionTemplateSettings';
import AdvancedLayoutSettings from './AdvancedLayoutSettings';
import SystemSettings from './SystemSettings';
import UserInvite from './UserInvite';

type SettingsTab = 'general' | 'branding' | 'invoice' | 'report' | 'prescription' | 'advanced' | 'system' | 'invite';

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as SettingsTab | null;
  const [activeTab, setActiveTab] = useState<SettingsTab>(tabParam || 'general');

  useEffect(() => {
    if (tabParam && ['general', 'branding', 'invoice', 'report', 'prescription', 'advanced', 'system', 'invite'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const tabs = [
    { id: 'general' as SettingsTab, name: 'General', icon: Building2 },
    { id: 'system' as SettingsTab, name: 'System Settings', icon: Cog },
    { id: 'invite' as SettingsTab, name: 'Invite Users', icon: UserPlus },
    { id: 'branding' as SettingsTab, name: 'Branding', icon: Palette },
    { id: 'invoice' as SettingsTab, name: 'Invoice Template', icon: FileText },
    { id: 'report' as SettingsTab, name: 'Report Template', icon: FileBarChart },
    { id: 'prescription' as SettingsTab, name: 'Prescription Template', icon: Stethoscope },
    { id: 'advanced' as SettingsTab, name: 'Advanced Layout', icon: SlidersHorizontal },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralSettings />;
      case 'system':
        return <SystemSettings />;
      case 'invite':
        return <UserInvite />;
      case 'branding':
        return <BrandingSettings />;
      case 'invoice':
        return <InvoiceTemplateSettings />;
      case 'report':
        return <ReportTemplateSettings />;
      case 'prescription':
        return <PrescriptionTemplateSettings />;
      case 'advanced':
        return <AdvancedLayoutSettings />;
      default:
        return <GeneralSettings />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Home className="w-4 h-4" />
          <span>/</span>
          <span className="text-gray-900 font-medium">Settings</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-none px-6 py-8">
        <div className="flex items-center space-x-3 mb-6">
          <SettingsIcon className="w-8 h-8 text-teal-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600">Configure hospital settings, branding, and templates</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-teal-600 text-teal-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
